import { AxiosInstance } from "axios"
import { BookingConfig, MeetingServiceConfig } from "./components/form/formTypes"
import {
    AccountDimensionDetailsForUser,
    AvailabilityDetails,
    BillReceiversItemMS,
    BookingOrderDetailsItem,
    CreateUpdateBooking,
    CreateUpdateBookingCalendarImportLinks,
    CreateUpdateBookingOrder,
    CreateUpdateMeetingServiceOrder,
    EventsForServiceCenter,
    ExtraServiceDetails,
    Message,
    ProductFavorite,
    ResourceFilterType,
    ResourceScheduleItem,
    ResourceType,
    SatelliteDetails,
    SaveProductFavoriteBody,
    ServiceCenterResourceDetails,
    ServiceCenterTimeline,
} from "./createOrderTypes"
import { ServiceResponse, ServiceResponseStatus } from "../../../core/api/apiTypes"
import { IPaymentResponse } from "../../../modules/payments/transactionTypes"
import { CompaniesUsersItem } from "../../../modules/users/userTypes"

export enum ConfigQuery {
    USER_DETAILS = "create-order-details-for-user",
    MEETING_SERVICE_CONFIG = "meeting-service-config",
    EVENTS_LIST_BY_DAY = "svcEventsByDay",
    TIMELINE_RESOURCES_FOR_USER = "timeline-resources-for-user",
    RESOURCE_FILTERS = "resource-filters",
    BOOKING_ORDER_DETAILS = "booking-order-details",
    EXTRA_SERVICES_FOR_USER = "extra-services-for-user",
    BOOKING_CONFIG = "booking-config",
    MEETING_SERVICE_MESSAGES = "meeting-service-messages",
    MEETING_SERVICE_BILL_RECEIVERS = "meeting-service-bill-receivers",
    USERS_FOR_COMPANY = "users-for-company",
    MEETING_SERVICE_SATELLITE_CANTEENS = "meeting-service-satellite-canteens",
    ACCOUNT_DIMENSION_DETAILS_FOR_USER = "account-dimension-details-for-user",
    RESOURCE_DETAILS = "resource-details",
    RESOURCES_FREE_BUSY = "resources-free-busy",
    SERVICE_CENTER_FREE_BUSY = "service-center-free-busy",
}

type MeetingServiceConfigurationParam = Readonly<{
    userId: number
    merchantId: number
}>

export type MeetingServiceConfigurationResponse = Readonly<{
    meetingServiceConfig: MeetingServiceConfig
}>

export async function fetchMeetingServiceConfiguration(
    { userId, merchantId }: MeetingServiceConfigurationParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<MeetingServiceConfigurationResponse> {
    const url = `/users/${userId}/merchants/${merchantId}/meetingService/config`
    const { data } = await axios.get(url, {
        signal,
        params: {},
    })

    return data
}

type TimelineResourcesParam = Readonly<{
    userId: number
    resourceTypes?: ResourceType | string
}>

export type TimelineResourcesResponse = Readonly<{
    serviceCenters: ServiceCenterTimeline[]
}>

export async function fetchTimelineResources(
    { userId, resourceTypes }: TimelineResourcesParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<TimelineResourcesResponse> {
    const url = `/users/${userId}/resources`
    const { data } = await axios.get(url, {
        signal,
        params: {
            resourceTypes,
        },
    })

    return data
}

type EventsForServiceCenterParam = Readonly<{
    userId: number
    serviceCenterId: string
    startDate: string
    endDate: string
}>

export type EventsForServiceCenterResponse = Readonly<{
    events: EventsForServiceCenter[]
}>

export async function fetchEventsForServiceCenter(
    { userId, serviceCenterId, startDate, endDate }: EventsForServiceCenterParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<EventsForServiceCenterResponse> {
    const url = `users/${userId}/serviceCenters/${serviceCenterId}/events`
    const { data } = await axios.get(url, {
        signal,
        params: {
            startDate,
            endDate,
        },
    })

    return data
}

export type ResourceFiltersResponse = Readonly<{
    filterGroups: ResourceFilterType[]
}>

export type ResourceFiltersParams = Readonly<{
    userId: number
}>

export async function fetchResourceFilters(
    { userId }: ResourceFiltersParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ResourceFiltersResponse> {
    const url = `/users/${userId}/resources/filterData`
    const { data } = await axios.get(url, {
        signal,
        params: {},
    })

    return data
}

type CreateBookingRequest = Readonly<{
    userId: number
    content: CreateUpdateBooking
}>

// Create / update booking
export type CreateUpdateBookingResponse = Readonly<{
    order: CreateUpdateBookingOrder
    calendarImportLinks: CreateUpdateBookingCalendarImportLinks[]
    // Present when booking a recurring series and some occurrences could not be booked because the
    // selected resources were busy (status PARTIAL_SUCCESS). `busyResourceDates` are ISO dates.
    serviceResponse?: Readonly<{
        displayMessage?: string
        status?: ServiceResponseStatus
        isUserMessage?: boolean
    }>
    resultDetails?: Readonly<{
        busyResourceDates?: string[]
    }>
}>

export async function createBooking(
    { userId, content }: CreateBookingRequest,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<CreateUpdateBookingResponse> {
    const { data } = await axios.post(
        `/users/${userId}/orders/bookings`,
        {
            ...content,
        },
        {
            signal,
        },
    )
    return data
}

type UpdateBookingRequest = Readonly<{
    userId: number
    orderId: number
    content: CreateUpdateBooking
}>

export async function updateBooking(
    { userId, orderId, content }: UpdateBookingRequest,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<CreateUpdateBookingResponse> {
    const { data } = await axios.post(
        `/users/${userId}/orders/bookings/${orderId}`,
        {
            ...content,
        },
        {
            signal,
        },
    )
    return data
}

type CreateMeetingServiceOrderRequest = Readonly<{
    userId: number
    content: CreateUpdateMeetingServiceOrder
}>

export type CreateMeetingServiceOrderResponse = Readonly<{
    order: {
        id: number
    }
}>

export async function createMeetingServiceOrder(
    { userId, content }: CreateMeetingServiceOrderRequest,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<CreateMeetingServiceOrderResponse> {
    const { data } = await axios.post(
        `/users/${userId}/orders/meetingService`,
        {
            order: {
                ...content,
            },
        },
        {
            signal,
        },
    )
    return data
}

type UpdateMeetingServiceOrderRequest = Readonly<{
    userId: number
    orderId: number
    content: CreateUpdateMeetingServiceOrder
}>

export async function updateMeetingServiceOrder(
    { userId, orderId, content }: UpdateMeetingServiceOrderRequest,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<CreateMeetingServiceOrderResponse> {
    const { data } = await axios.post(
        `/users/${userId}/orders/meetingService/${orderId}`,
        {
            order: {
                ...content,
            },
        },
        {
            signal,
        },
    )
    return data
}

type FreeBusyScheduleParams = Readonly<{
    serviceCenterUid: string
    availabilityViewInterval: number //Defines the interval to display the availabilityView. The interval can be 5, 10, 15, 20 or 30.
    startTime: string
    endTime: string
}>

export type FreeBusyScheduleResponse = Readonly<{
    availabilityDetails: AvailabilityDetails
    resources: ResourceScheduleItem[]
}>

export async function fetchFreeBusyScheduleForServiceCenter(
    { serviceCenterUid, ...rest }: FreeBusyScheduleParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FreeBusyScheduleResponse> {
    const url = `/serviceCenters/byUUID/${serviceCenterUid}/freeBusy`
    const { data } = await axios.get(url, {
        signal,
        params: { ...rest },
    })

    return data
}

type OrderStatusParams = Readonly<{
    orderUid: string
}>

export async function fetchOrderStatus(
    { orderUid }: OrderStatusParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<IPaymentResponse> {
    const url = `/orders/byUUID/${orderUid}/payment`
    const { data } = await axios.get(url, {
        signal,
    })

    return data
}

export async function fetchPublicOrderStatus(
    { orderUid }: OrderStatusParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<IPaymentResponse> {
    const url = `/public/orders/byUUID/${orderUid}/payment`
    const { data } = await axios.get(url, {
        signal,
    })

    return data
}

type BookingOrderDetailsParam = Readonly<{
    orderId: number
}>

export type BookingOrderDetailsResponse = Readonly<{
    order: BookingOrderDetailsItem
}>

export async function fetchBookingOrderDetails(
    { orderId }: BookingOrderDetailsParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<BookingOrderDetailsResponse> {
    const url = `/orders/${orderId}`
    const { data } = await axios.get(url, {
        signal,
        params: {},
    })

    return data
}

// FAVORITES

export type SaveProductFavoriteResponse = Readonly<{
    product: ProductFavorite
}>

export async function saveProductFavorite(
    content: SaveProductFavoriteBody,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<SaveProductFavoriteResponse> {
    const { data } = await axios.post(
        `/organization/company/user/productFavorites`,
        {
            ...content,
        },
        {
            signal,
        },
    )
    return data
}

export async function deleteProductFavorite(
    { productId, body }: { productId: number; body?: Record<string, unknown> },
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { data } = await axios.delete(`/organization/company/user/productFavorites/${productId}`, {
        data: body,
        signal,
    })
    return data
}

type ExtraServicesForUserParam = Readonly<{
    userId: number
    merchantId: number
    deliveryType?: string
    deliveryTime?: string
    priceListId?: number
}>

export type ExtraServicesForUserResponse = Readonly<{
    extraServices: ExtraServiceDetails[]
}>

export async function fetchExtraServicesForUser(
    { userId, merchantId, deliveryType, deliveryTime, priceListId }: ExtraServicesForUserParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ExtraServicesForUserResponse> {
    const url = `/users/${userId}/merchants/${merchantId}/meetingService/extraServices`
    const { data } = await axios.get(url, {
        signal,
        params: {
            deliveryType,
            deliveryTime,
            priceListId,
        },
    })

    return data
}

export type BookingConfigurationResponse = Readonly<{
    bookingConfig: BookingConfig
}>

export async function fetchBookingConfiguration(
    { userId }: { userId: number },
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<BookingConfigurationResponse> {
    const url = `/users/${userId}/booking/config`
    const { data } = await axios.get(url, {
        signal,
        params: {},
    })

    return data
}

export type UsersForCompanyParams = Readonly<{
    userId: number
    companyId: number
    search?: string
}>

export type UsersForCompanyResponse = Readonly<{
    users: CompaniesUsersItem[]
}>

export async function fetchUsersForCompany(
    { userId, companyId, search }: UsersForCompanyParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<UsersForCompanyResponse> {
    const url = `users/${userId}/companies/${companyId}/users`
    const { data } = await axios.get(url, {
        params: {
            search,
        },
        signal,
    })

    return data
}

type MeetingServiceMessagesParam = Readonly<{
    merchantId: number
    shopId: number
    priceListId: number
}>

export type MeetingServiceMessagesResponse = Readonly<{
    messages: Message[]
}>

export async function fetchMeetingServiceMessages(
    { merchantId, ...rest }: MeetingServiceMessagesParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<MeetingServiceMessagesResponse> {
    const url = `/merchants/${merchantId}/meetingService/messages`
    const { data } = await axios.get(url, {
        signal,
        params: { ...rest },
    })

    return data
}

type FreeBusyScheduleForResourcesParams = Readonly<{
    userId: number
    availabilityViewInterval: number //Defines the interval to display the availabilityView. The interval can be 5, 10, 15, 20 or 30.
    startTime: string // 2025-01-12T11:00
    endTime: string
    resourceIds: string // 1,4
}>

export type FreeBusyScheduleForResourcesResponse = Readonly<{
    availabilityDetails: AvailabilityDetails
    resources: ResourceScheduleItem[]
}>

export async function fetchFreeBusyScheduleForResources(
    { userId, ...rest }: FreeBusyScheduleForResourcesParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FreeBusyScheduleForResourcesResponse> {
    const url = `/users/${userId}/freeBusy`
    const { data } = await axios.get(url, {
        signal,
        params: { ...rest },
    })

    return data
}

export type BillReceiversParams = Readonly<{
    userId: number
    endpoint?: string
    search?: string
}>

export type BillReceiversResponse = Readonly<{
    billReceivers: BillReceiversItemMS[]
}>

export async function fetchBillReceiversMS(
    { userId, endpoint, search }: BillReceiversParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<BillReceiversResponse> {
    const url = `/users/${userId}/payment/billReceivers`
    const { data } = await axios.get(url, {
        signal,
        params: {
            ...(endpoint ? { endpoint } : {}),
            ...(search ? { search } : {}),
        },
    })

    return data
}

export type SatelliteCanteensParams = Readonly<{
    userId: number
    merchantId: number
    type: string
}>

export type SatelliteCanteensResponse = Readonly<{
    satellites: SatelliteDetails[]
}>

export async function fetchSatelliteCanteens(
    { userId, merchantId, type }: SatelliteCanteensParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<SatelliteCanteensResponse> {
    const url = `/users/${userId}/merchants/${merchantId}/satellites`
    const { data } = await axios.get(url, {
        signal,
        params: {
            type,
        },
    })

    return data
}

type DimensionDetailsForUserParams = Readonly<{
    userId: number
    dimensionId: number
    search?: string
}>
export type DimensionDetailsForUserResponse = Readonly<{
    accountDimension: AccountDimensionDetailsForUser
}>

export async function fetchDimensionDetailsForUser(
    { userId, dimensionId, search }: DimensionDetailsForUserParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<DimensionDetailsForUserResponse> {
    const url = `/users/${userId}/payment/dimensions/${dimensionId}`
    const { data } = await axios.get(url, {
        signal,
        params: {
            search,
        },
    })

    return data
}

type ServiceCenterResourceDetailsParams = Readonly<{
    id: string
}>

export type ServiceCenterResourceDetailsResponse = Readonly<{
    resources: ServiceCenterResourceDetails[]
}>

export async function fetchServiceCenterResourceDetails(
    { id }: ServiceCenterResourceDetailsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceCenterResourceDetailsResponse> {
    const url = `/servicecenter/resources/${id}`
    const { data } = await axios.get(url, {
        signal,
        params: {},
    })

    return data
}
